import { memo } from "react";

import { busbarMaterialSchema, voltageDropBasisSchema } from "../services/busbarSelectionContract";
import {
  BUSBAR_MATERIAL_LABELS,
  VOLTAGE_DROP_BASIS_LABELS,
  type BusbarSelectionDraft,
  type BuswayRowDraft,
} from "./busbarSelectionDraft";
import {
  anyFilled,
  blockMemo,
  fieldKit,
  OptionalBlock,
  type BlockProps,
} from "./BusbarSelectionFields";

type BuswayRowProps = {
  row: BuswayRowDraft;
  // Zero-based position in the list; shown to the user as "Busway row 1".
  index: number;
  onChange: (rowId: string, patch: Partial<BuswayRowDraft>) => void;
  onRemove: (rowId: string) => void;
};

// One catalogue row of a compact busway, as the engineer enters it. The row holds no state of
// its own: every edit goes up as a patch with the row's id.
export const BuswayRow = memo(function BuswayRow({
  row,
  index,
  onChange,
  onRemove,
}: BuswayRowProps) {
  const number = index + 1;
  const kit = fieldKit(
    row,
    (patch) => onChange(row.id, patch),
    (field) => `busway-${number}-${field}`,
  );

  return (
    <fieldset data-busway-row={row.id}>
      <legend>Busway row {number}</legend>
      {kit.decimal("Rated current (A)", "ratedCurrentA")}
      {kit.choice(
        "Conductor material",
        "material",
        busbarMaterialSchema.options,
        BUSBAR_MATERIAL_LABELS,
      )}
      {kit.decimal("Icw (kA)", "icwKa")}
      {kit.decimal("Icw duration (s)", "icwDurationS")}
      {kit.decimal("Ipk (kA)", "ipkKa")}
      {kit.decimal("Voltage drop (mV/A/m)", "voltageDropMvPerAM")}
      {kit.text("Source", "source")}
      {kit.choice(
        "Voltage drop basis",
        "voltageDropBasis",
        voltageDropBasisSchema.options,
        VOLTAGE_DROP_BASIS_LABELS,
      )}
      {kit.reference(["document", "revisionOrDate", "pageOrClause"])}
      <button type="button" onClick={() => onRemove(row.id)}>
        Remove busway row {number}
      </button>
    </fieldset>
  );
});

const BUSWAY_KEYS = [
  "buswayRows",
  "buswayRouteLengthM",
  "buswayAllowableVoltageDropPercent",
] as const;

type BuswayValues = Pick<BusbarSelectionDraft, (typeof BUSWAY_KEYS)[number]>;

type BuswayBlockProps = BlockProps<BuswayValues> & {
  onAddRow: () => void;
  onRowChange: (rowId: string, patch: Partial<BuswayRowDraft>) => void;
  onRowRemove: (rowId: string) => void;
};

// Shown for a BUSWAY study only; the rows stay in the draft while it is hidden.
export const BuswayBlock = blockMemo(function BuswayBlock({
  draft,
  onChange,
  disabled,
  onAddRow,
  onRowChange,
  onRowRemove,
}: BuswayBlockProps) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Busway"
      filled={
        anyFilled(draft.buswayRouteLengthM, draft.buswayAllowableVoltageDropPercent) ||
        draft.buswayRows.length > 0
      }
      disabled={disabled}
    >
      <p>
        <strong>Busway rows</strong>
      </p>
      {draft.buswayRows.map((row, index) => (
        <BuswayRow
          key={row.id}
          row={row}
          index={index}
          onChange={onRowChange}
          onRemove={onRowRemove}
        />
      ))}
      <button type="button" onClick={onAddRow}>
        Add busway row
      </button>
      {kit.decimal("Route length (m)", "buswayRouteLengthM")}
      {kit.decimal("Allowable voltage drop (%)", "buswayAllowableVoltageDropPercent")}
    </OptionalBlock>
  );
}, BUSWAY_KEYS);
